import { message, Modal } from 'antd';
import { GlossaryNode, GlossaryTerm } from '../../../../types.generated';
import { ChildGlossaryTermFragment } from '../../../../graphql/glossaryNode.generated';
import { GlossaryNodeFragment } from '../../../../graphql/fragments.generated';
import i18n from '../../../../i18n/i18n';

interface BulkDeleteGlossaryEntitiesParams {
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    batchRemoveGlossaryMutation: any;
    selectedItems: string[];
    onSuccess?: () => void;
    refetch?: () => Promise<any>;
    setSelectedItems: (items: string[]) => void;
    setIsDeleting?: (isDeleting: boolean) => void;
}

/**
 * Handles the bulk deletion of the selected glossary terms.
 * Uses the batchRemoveGlossaryMutation to delete each of the selected glossary terms.
 * Displays a notification message to the user once the deletion is completed.
 * Clears the selectedItems state after completing the operation.
 */
export const bulkDeleteGlossaryEntities = ({
    nodes,
    terms,
    selectedItems,
    batchRemoveGlossaryMutation,
    setSelectedItems,
    setIsDeleting,
    refetch,
    onSuccess,
}: BulkDeleteGlossaryEntitiesParams) => {
    // Filter selected items into terms and nodes
    const selectedTerms = terms?.filter((term) => selectedItems.includes(term?.urn || '')) || [];
    const selectedNodes = nodes?.filter((node) => selectedItems.includes(node?.urn || '')) || [];

    const totalSelected = selectedTerms.length + selectedNodes.length;

    if (totalSelected === 0) {
        message.info(i18n.t('No glossary entities were selected for deletion.'));
        return;
    }

    let confirmMessage = '';
    let titleMessage = i18n.t('Delete Glossary Entities');

    if (selectedTerms.length > 0 && selectedNodes.length > 0) {
        confirmMessage = i18n.t(
            'Are you sure you want to delete {{count}} glossary term(s) and {{count2}} term group(s)? This action cannot be undone.',
            {
                count: selectedTerms.length,
                count2: selectedNodes.length,
            },
        );
    } else if (selectedTerms.length > 0) {
        titleMessage = i18n.t('Delete Glossary Terms');
        confirmMessage = i18n.t(
            'Are you sure you want to delete {{count}} glossary term(s)? This action cannot be undone.',
            {
                count: selectedTerms.length,
            },
        );
    } else {
        titleMessage = i18n.t('Delete Term Groups');
        confirmMessage = i18n.t(
            'Are you sure you want to delete {{count}} term group(s)? This action cannot be undone.',
            {
                count: selectedNodes.length,
            },
        );
    }

    Modal.confirm({
        title: titleMessage,
        content: confirmMessage,
        cancelText: i18n.t('No'),
        className: 'f-borderless-modal',
        okText: i18n.t('Delete'),
        style: {
            maxWidth: 400,
        },
        okButtonProps: {
            type: undefined,
            danger: true,
        },
        maskClosable: true,
        closable: true,
        centered: true,
        onOk: async () => {
            setIsDeleting?.(true);

            try {
                // Combine all entities to delete
                const allEntitiesToDelete = [...selectedTerms, ...selectedNodes];

                await batchRemoveGlossaryMutation({
                    variables: {
                        urn: allEntitiesToDelete.map((entity) => entity?.urn || ''),
                    },
                });

                message.success(
                    i18n.t(`Successfully deleted {{count}} glossary {{entity}}.`, {
                        count: totalSelected,
                        entity: totalSelected === 1 ? i18n.t('entity') : i18n.t('entities'),
                    }),
                );

                // Refetch data to update the UI
                refetch?.();
                onSuccess?.();
            } catch (error: any) {
                message.error(i18n.t(`Failed to delete glossary entities: ${error?.message}`));
            } finally {
                setIsDeleting?.(false);
                setSelectedItems([]);
            }
        },
    });
};
